import React, { useState, useEffect } from 'react';
import { orderAPI } from '../services/marketplaceAPI';
import { api } from '../utils/api';
import ReportViewer from './ReportViewer';
import PrescriptionList from './prescription/PrescriptionList';

const ReportsSection = () => {
  const [labTestOrders, setLabTestOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('lab-reports');
  const [deleting, setDeleting] = useState({});

  const [reportViewer, setReportViewer] = useState({
    isOpen: false,
    reportUrl: '',
    reportName: ''
  });

  useEffect(() => {
    fetchLabTestOrders();
  }, []);

  const fetchLabTestOrders = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await orderAPI.getUserOrders({ limit: 50 });
      console.log('Orders response:', response);
      
      if (response.success) {
        // Filter only lab test orders with reports
        const allLabOrders = response.data.orders.filter(order => 
          order.items.some(item => item.category === 'lab-test')
        );
        
        // Log for debugging
        console.log('All lab test orders:', allLabOrders.length);
        allLabOrders.forEach(order => {
          console.log(`Order ${order.orderId}:`, {
            hasReportPaths: !!order.reportPaths,
            reportPathsCount: order.reportPaths?.length || 0,
            reportPaths: order.reportPaths,
            items: order.items.filter(item => item.category === 'lab-test').map(item => ({
              name: item.name,
              productId: item.productId?._id || item.productId
            }))
          });
        });
        
        const labOrders = allLabOrders.filter(order => 
          order.reportPaths && order.reportPaths.length > 0
        );
        
        console.log('Lab orders with reports:', labOrders.length);
        setLabTestOrders(labOrders);
      } else {
        setError('Failed to fetch lab test reports');
      }
    } catch (err) {
      console.error('Error fetching lab test reports:', err);
      setError('Error loading lab test reports. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewReport = (order, labTestItem) => {
    // Check if report exists in reportPaths
    const productId = labTestItem.productId?._id || labTestItem.productId;
    const reportPath = order.reportPaths?.find(rp => {
      const rpProductId = rp.productId?._id || rp.productId;
      return String(rpProductId) === String(productId);
    });
    
    if (reportPath) {
      // Use authenticated API call to get the report URL
      const reportUrl = `${api.defaults.baseURL}/reports/${order.orderId}/${productId}`;
      setReportViewer({
        isOpen: true,
        reportUrl: reportUrl,
        reportName: `${labTestItem.name} - ${order.orderId}`
      });
    } else {
      alert('Report not available yet. Please wait for the report to be generated.');
    }
  };

  const handleDeleteReport = async (order, labTestItem) => {
    // Confirm deletion
    const confirmDelete = window.confirm(
      `Are you sure you want to delete the report for "${labTestItem.name}"?\n\nThis action cannot be undone.`
    );
    
    if (!confirmDelete) {
      return;
    }
    
    try {
      const productId = labTestItem.productId?._id || labTestItem.productId;
      const deleteKey = `${order.orderId}-${productId}`;
      
      setDeleting(prev => ({ ...prev, [deleteKey]: true }));
      
      // Ensure token is available
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Authentication required. Please log in again.');
        return;
      }
      
      // Delete the report
      await api.delete(`/reports/${order.orderId}/${productId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Remove the report from the local state
      setLabTestOrders(prevOrders => {
        return prevOrders.map(o => {
          if (o._id === order._id) {
            // Remove the reportPath entry
            const updatedOrder = {
              ...o,
              reportPaths: o.reportPaths.filter(rp => {
                const rpProductId = rp.productId?._id || rp.productId;
                return String(rpProductId) !== String(productId);
              })
            };
            return updatedOrder;
          }
          return o;
        }).filter(o => {
          // Remove orders that no longer have any reports
          const hasLabTests = o.items.some(item => item.category === 'lab-test');
          if (!hasLabTests) return true;
          return o.reportPaths && o.reportPaths.length > 0;
        });
      });
      
      alert('Report deleted successfully');
    } catch (error) {
      console.error('Error deleting report:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        alert('Authentication failed. Please log in again.');
      } else if (error.response?.status === 404) {
        alert('Report not found. It may have already been deleted.');
      } else if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert('Failed to delete report. Please try again.');
      }
    } finally {
      const productId = labTestItem.productId?._id || labTestItem.productId;
      const deleteKey = `${order.orderId}-${productId}`;
      setDeleting(prev => {
        const newState = { ...prev };
        delete newState[deleteKey];
        return newState;
      });
    }
  };

  const handleDownloadReport = async (order, labTestItem) => {
    try {
      // Check if report exists in reportPaths
      const productId = labTestItem.productId?._id || labTestItem.productId;
      const reportPath = order.reportPaths?.find(rp => {
        const rpProductId = rp.productId?._id || rp.productId;
        return String(rpProductId) === String(productId);
      });
      
      if (reportPath) {
        // Ensure token is available
        const token = localStorage.getItem('token');
        if (!token) {
          alert('Authentication required. Please log in again.');
          return;
        }
        
        // Fetch the report with authentication
        const response = await api.get(`/reports/${order.orderId}/${productId}/download`, {
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        // Create blob and download
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `lab-report-${order.orderId}-${labTestItem.name}.pdf`;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } else {
        alert('Report not available yet. Please wait for the report to be generated.');
      }
    } catch (error) {
      console.error('Error downloading report:', error);
      console.error('Error response:', error.response);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        alert('Authentication failed. Please log in again.');
      } else if (error.response?.status === 404) {
        alert('Report file not found. Please contact support.');
      } else if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert('Failed to download report. Please try again.');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Group orders by date
  const groupedOrders = labTestOrders.reduce((groups, order) => {
    const date = formatDate(order.orderDate);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(order);
    return groups;
  }, {});

  if (loading) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: '#6b7280'
      }}>
        Loading reports...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: '#dc2626',
        background: '#fee2e2',
        borderRadius: '0.5rem',
        border: '1px solid #fecaca'
      }}>
        ⚠️ {error}
      </div>
    );
  }

  return (
    <div>
      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '2rem',
        borderBottom: '2px solid #e5e7eb'
      }}>
        <button
          onClick={() => setActiveTab('lab-reports')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            background: activeTab === 'lab-reports' ? '#3b82f6' : 'transparent',
            color: activeTab === 'lab-reports' ? 'white' : '#6b7280',
            borderRadius: '0.5rem 0.5rem 0 0',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.2s ease'
          }}
        >
          🧪 Lab Test Reports
        </button>
        <button
          onClick={() => setActiveTab('prescriptions')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            background: activeTab === 'prescriptions' ? '#3b82f6' : 'transparent',
            color: activeTab === 'prescriptions' ? 'white' : '#6b7280',
            borderRadius: '0.5rem 0.5rem 0 0',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.2s ease'
          }}
        >
          💊 My Prescriptions
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'lab-reports' && (
        <div>
          <h3 className="lab-reports-title">
            📋 Lab Test Reports
          </h3>

          {Object.keys(groupedOrders).length === 0 ? (
            <div className="no-lab-reports-message">
              📄 No lab test reports available yet
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {Object.entries(groupedOrders).map(([date, orders]) => (
              <div key={date} style={{
                background: 'white',
                borderRadius: '1rem',
                padding: '1.5rem',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  📅 {date}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {orders.map((order) => {
                    const labTestItems = order.items.filter(item => item.category === 'lab-test');
                    
                    return labTestItems.map((item, index) => {
                      const productId = item.productId?._id || item.productId;
                      const reportPath = order.reportPaths?.find(rp => {
                        const rpProductId = rp.productId?._id || rp.productId;
                        return String(rpProductId) === String(productId);
                      });
                      const hasReport = !!reportPath;
                  
                      return (
                        <div key={`${order._id}-${index}`} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '1rem',
                          background: '#f8fafc',
                          borderRadius: '0.75rem',
                          border: '1px solid #e5e7eb'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem'
                          }}>
                            <span style={{ fontSize: '1.2rem' }}>🧪</span>
                            <div>
                              <div style={{
                                fontSize: '1rem',
                                fontWeight: '600',
                                color: '#374151'
                              }}>
                                {item.name}
                              </div>
                              <div style={{
                                fontSize: '0.875rem',
                                color: '#6b7280'
                              }}>
                                Order #{order.orderId}
                              </div>
                              {/* Display selected lab option */}
                              {item.labOption && (
                                <div style={{
                                  fontSize: '0.8rem',
                                  color: '#3b82f6',
                                  fontWeight: '500',
                                  marginTop: '0.25rem'
                                }}>
                                  🏥 {item.labOption.labName}
                                </div>
                              )}
                            </div>
                          </div>

                          <div style={{
                            display: 'flex',
                            gap: '0.5rem',
                            flexWrap: 'wrap'
                          }}>
                            <button
                              onClick={() => handleViewReport(order, item)}
                              disabled={!hasReport}
                              style={{
                                padding: '0.5rem 1rem',
                                background: hasReport 
                                  ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                                  : '#e5e7eb',
                                color: hasReport ? 'white' : '#9ca3af',
                                border: 'none',
                                borderRadius: '0.5rem',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                cursor: hasReport ? 'pointer' : 'not-allowed',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}
                              onMouseEnter={(e) => {
                                if (hasReport) {
                                  e.target.style.transform = 'translateY(-1px)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (hasReport) {
                                  e.target.style.transform = 'translateY(0)';
                                }
                              }}
                            >
                              👁️ View
                            </button>

                            <button
                              onClick={() => handleDownloadReport(order, item)}
                              disabled={!hasReport}
                              style={{
                                padding: '0.5rem 1rem',
                                background: hasReport 
                                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                  : '#e5e7eb',
                                color: hasReport ? 'white' : '#9ca3af',
                                border: 'none',
                                borderRadius: '0.5rem',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                cursor: hasReport ? 'pointer' : 'not-allowed',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}
                              onMouseEnter={(e) => {
                                if (hasReport) {
                                  e.target.style.transform = 'translateY(-1px)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (hasReport) {
                                  e.target.style.transform = 'translateY(0)';
                                }
                              }}
                            >
                              ⬇️ Download
                            </button>

                            <button
                              onClick={() => handleDeleteReport(order, item)}
                              disabled={!hasReport || deleting[`${order.orderId}-${productId}`]}
                              style={{
                                padding: '0.5rem 1rem',
                                background: hasReport && !deleting[`${order.orderId}-${productId}`]
                                  ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                                  : '#e5e7eb',
                                color: hasReport && !deleting[`${order.orderId}-${productId}`] ? 'white' : '#9ca3af',
                                border: 'none',
                                borderRadius: '0.5rem',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                cursor: hasReport && !deleting[`${order.orderId}-${productId}`] ? 'pointer' : 'not-allowed',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}
                              onMouseEnter={(e) => {
                                if (hasReport && !deleting[`${order.orderId}-${productId}`]) {
                                  e.target.style.transform = 'translateY(-1px)';
                                  e.target.style.background = 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (hasReport && !deleting[`${order.orderId}-${productId}`]) {
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
                                }
                              }}
                            >
                              {deleting[`${order.orderId}-${productId}`] ? '⏳ Deleting...' : '🗑️ Delete'}
                            </button>
                          </div>
                        </div>
                      );
                    });
                  })}
                </div>
              </div>
            ))}
            </div>
          )}
        </div>
      )}

      {/* Prescriptions Tab */}
      {activeTab === 'prescriptions' && (
        <div>
          <PrescriptionList userRole="patient" />
        </div>
      )}

      {/* Report Viewer Modal */}
      <ReportViewer
        isOpen={reportViewer.isOpen}
        onClose={() => setReportViewer({ isOpen: false, reportUrl: '', reportName: '' })}
        reportUrl={reportViewer.reportUrl}
        reportName={reportViewer.reportName}
      />
    </div>
  );
};

export default ReportsSection;
